import { supabase } from '@/integrations/supabase/client';
import { setCurrentSupabaseOrganization } from '@/lib/supabaseOrganization';
import type { IPaymentPort } from '../ports/payment.port';
import type { Payment, AddPaymentData, DashboardPayments, PaymentStatus } from '../types/payment.types';

export class PaymentRepository implements IPaymentPort {
  async fetchPayments(orgId: string): Promise<Payment[]> {
    await setCurrentSupabaseOrganization(orgId);

    const { data, error } = await supabase.rpc('get_payments_summary');
    if (error) {
      console.error('Error fetching payments:', error);
      throw error;
    }

    return (data || []).map((row: any) => {
      const name = row.influencer_name ?? '';
      const initials = name
        .split(' ')
        .map((s: string) => s[0])
        .join('')
        .toUpperCase();

      return {
        id: row.id,
        influencer: {
          name,
          avatar: initials,
          profilePic: row.influencer_profile_pic ?? null,
        },
        campaign: row.campaign_name,
        campaignId: row.campaign_id,
        baseFee: Number(row.base_fee),
        gstApplied: row.gst_applied,
        gstAmount: Number(row.gst_amount),
        tdsDeducted: row.tds_deducted,
        tdsAmount: Number(row.tds_amount),
        netPayable: Number(row.net_payable),
        fee: Number(row.net_payable),
        status: row.status,
        dueDate: row.due_date,
        paymentDate: row.payment_date,
        paymentMode: row.payment_mode,
        hasInvoice: row.has_invoice,
        createdAt: row.created_at,
        reference: row.reference,
        notes: row.notes,
        invoiceUrl: row.invoice_url,
      };
    });
  }

  async fetchCampaignPayments(campaignId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('payments')
      .select(`
        *,
        campaign_influencers!inner (
          campaign_id,
          deliverables,
          status,
          influencers (
            id,
            name,
            handle,
            platform
          )
        )
      `)
      .eq('campaign_influencers.campaign_id', campaignId);

    if (error) {
      console.error('Error fetching campaign payments:', error);
      throw error;
    }

    return data || [];
  }

  async fetchDashboardPayments(orgId: string): Promise<DashboardPayments> {
    await setCurrentSupabaseOrganization(orgId);

    const { data, error } = await supabase.rpc('get_dashboard_payment_summary');
    if (error) {
      console.error('Error fetching dashboard payments summary:', error);
      throw error;
    }

    const summary = data as unknown as {
      total_budget: number;
      paid_amount: number;
      outstanding_amount: number;
      overdue_payments: number;
    };

    return {
      totalBudget: summary.total_budget ?? 0,
      paidAmount: summary.paid_amount ?? 0,
      outstandingAmount: summary.outstanding_amount ?? 0,
      overduePayments: summary.overdue_payments ?? 0,
    };
  }

  async addPayment(orgId: string, paymentData: AddPaymentData): Promise<any> {
    const paymentWithOrg = {
      ...paymentData,
      organization_id: orgId,
    };

    const { data, error } = await supabase
      .from('payments')
      .insert(paymentWithOrg as any)
      .select(`
        *,
        campaign_influencers!inner (
          influencers (
            id,
            name,
            handle,
            platform
          ),
          campaigns (
            id,
            name
          )
        )
      `)
      .single();

    if (error) {
      console.error('Error adding payment:', error);
      throw error;
    }

    return data;
  }

  async markPaymentPaid(paymentId: string): Promise<void> {
    const { error } = await supabase
      .from('payments')
      .update({
        status: 'paid',
        payment_date: new Date().toISOString().split('T')[0],
        updated_at: new Date().toISOString(),
      })
      .eq('id', paymentId);

    if (error) {
      console.error('Error marking payment paid:', error);
      throw error;
    }
  }

  async bulkMarkPaymentsPaid(paymentIds: string[]): Promise<number> {
    const { error } = await supabase
      .from('payments')
      .update({
        status: 'paid',
        payment_date: new Date().toISOString().split('T')[0],
        updated_at: new Date().toISOString(),
      })
      .in('id', paymentIds);

    if (error) {
      console.error('Error bulk marking payments paid:', error);
      throw error;
    }

    return paymentIds.length;
  }

  async updateOverduePayments(paymentIds: string[]): Promise<void> {
    const { error } = await supabase
      .from('payments')
      .update({ status: 'overdue', updated_at: new Date().toISOString() })
      .in('id', paymentIds);

    if (error) {
      console.error('Error updating overdue payments:', error);
      throw error;
    }
  }

  async updatePaymentStatus(paymentId: string, status: PaymentStatus): Promise<any> {
    const { data, error } = await supabase
      .from('payments')
      .update({
        status: status,
        payment_date: status === 'paid' ? new Date().toISOString().split('T')[0] : null,
      })
      .eq('id', paymentId);

    if (error) {
      console.error('Error updating payment status:', error);
      throw error;
    }

    return data;
  }
}

export const paymentRepository = new PaymentRepository();
